<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Official Withdrawals</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by User Name, Email ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.status = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="900"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>&nbsp; Sr. No.</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">User</th>
                      <th class="text-left">Email</th>
                      <th class="text-left">Crystal Amount</th>
                      <th class="text-left">Dollar Amount</th>
                      <th class="text-left">Account Details</th>
                      <th class="text-left">Date</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="withdrawals.length != 0">
                    <tr
                      v-for="(withdrawal, i) in withdrawals"
                      :key="`withdrawal_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': withdrawals.length - 1 == i,
                      }"
                    >
                      <td>&nbsp;{{ i + 1 }}</td>
                      <td>
                        <v-tooltip bottom v-if="withdrawal.status == 'approved'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Approved
                            </v-chip>
                          </template>
                          <span>Withdrawal is Approved</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="withdrawal.status == 'pending'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="orange"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-clock-outline</v-icon>
                              </v-avatar>
                              Pending
                            </v-chip>
                          </template>
                          <span>Withdrawal is Pending</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="withdrawal.status == 'rejected'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Rejected
                            </v-chip>
                          </template>
                          <span>Withdrawal is Rejected</span>
                        </v-tooltip>
                      </td>

                      <td>
                        {{
                          withdrawal.user
                            ? withdrawal.user.first_name +
                              " " +
                              withdrawal.user.last_name
                            : ""
                        }}
                      </td>
                      <td>
                        {{ withdrawal.user ? withdrawal.user.email : "" }}
                      </td>
                      <td>
                        {{ withdrawal.crystal_amount || "" }}
                      </td>
                      <td>
                        ${{ withdrawal.doller_amount || "" }}
                      </td>
                      <td>
                        {{ withdrawal.account_details || "" }}
                      </td>
                      <td>
                        {{ formattedDate(withdrawal.created_at) || "" }}
                      </td>
                      <td class="text-right">
                        <template v-if="withdrawal.status == 'pending'">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="success"
                                @click="updateStatus(withdrawal.id, 'approved')"
                                :loading="isUpdating[withdrawal.id]"
                              >
                                <v-icon dark> mdi-check </v-icon>
                              </v-btn>
                            </template>
                            <span>Approve this Withdrawal</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto ml-2"
                                color="error"
                                @click="updateStatus(withdrawal.id, 'rejected')"
                                :loading="isUpdating[withdrawal.id]"
                              >
                                <v-icon dark> mdi-close </v-icon>
                              </v-btn>
                            </template>
                            <span>Reject this Withdrawal</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <span class="text-caption grey--text"
                            >Already {{ withdrawal.status }}</span
                          >
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Success Dialog Box -->
      <v-dialog v-model="show_success_dialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>{{ success_message }}</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="show_success_dialog = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "officialWithdrawalsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
      },
      withdrawals: [],
      statuses: [
        { text: "All", value: "" },
        { text: "Pending", value: "pending" },
        { text: "Approved", value: "approved" },
        { text: "Rejected", value: "rejected" },
      ],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      isUpdating: {},
      show_success_dialog: false,
      success_message: "",
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let query = "";

      if (this.filter.status) {
        query += `status=${this.filter.status}&`;
      }

      try {
        const response = await axios.get(
          `crystal-wallet/official-withdrawals?${query}`
        );

        console.log("Official Withdrawals API Response:", response.data);

        if (response.data.success) {
          this.withdrawals = response.data.data;

          // Filter by search keyword on frontend if provided
          if (this.filter.search_keyword) {
            const keyword = this.filter.search_keyword.toLowerCase();
            this.withdrawals = this.withdrawals.filter((w) => {
              return (
                (w.user &&
                  (w.user.first_name?.toLowerCase().includes(keyword) ||
                    w.user.last_name?.toLowerCase().includes(keyword) ||
                    w.user.email?.toLowerCase().includes(keyword) ||
                    w.user.user_name?.toLowerCase().includes(keyword))) ||
                w.account_details?.toLowerCase().includes(keyword)
              );
            });
          }

          this.count = this.withdrawals.length;
        } else {
          console.error("API returned success: false", response.data);
        }
      } catch (error) {
        console.error("Official Withdrawals API Error:", error);
        if (error.response) {
          console.error("Error Status:", error.response.status);
          console.error("Error Data:", error.response.data);

          if (error.response.status === 401) {
            alert("Authentication required. Please login again.");
            this.$router.push("/login");
          } else if (error.response.status === 403) {
            alert("Access denied. You don't have permission to view this.");
          } else {
            alert(
              error.response.data?.message ||
              "Failed to load official withdrawals"
            );
          }
        } else if (error.request) {
          console.error("No response received:", error.request);
          alert("Network error. Please check your connection and try again.");
        } else {
          console.error("Request setup error:", error.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async updateStatus(withdrawalId, status) {
      this.$set(this.isUpdating, withdrawalId, true);

      await axios
        .post(`crystal-wallet/official-withdrawal/approve`, {
          withdrawal_id: withdrawalId,
          status: status,
        })
        .then((response) => {
          if (response.data.success) {
            this.success_message = response.data.message;
            this.show_success_dialog = true;
            this.getData();
          }
        })
        .catch((error) => {
          console.log(error);
          alert(
            error.response?.data?.error ||
              "Failed to update withdrawal status"
          );
        })
        .finally(() => {
          this.$set(this.isUpdating, withdrawalId, false);
        });
    },
    formattedDate(date) {
      const dateObj = new Date(date);
      return dateObj.toISOString().slice(0, 10); // Formats the date
    },
  },
};
</script>
